<script lang="ts">
  /**
   * Chiffrer ce que la table a inventé.
   *
   * Une Affliction maison, un objet que le meneur a sorti de son chapeau : le
   * livre ne les connaît pas, et jusqu'ici leurs effets vivaient dans un
   * libellé en prose — « Brûler −10 CHA −30 Séduction +10 Intimidation ».
   * Trois chiffres justes, qu'aucun jet ne voyait, et que le joueur devait
   * retenir seul.
   *
   * Ce qu'on saisit ici entre dans le même vocabulaire que les catalogues du
   * livre. Le tiroir de jet l'explique alors ligne par ligne, au même titre
   * qu'un Statut ou qu'une blessure.
   *
   * # Un chiffre se corrige sans se refaire
   *
   * La première version n'offrait qu'effacer puis resaisir : pour passer un
   * −10 à −20 il fallait rouvrir le formulaire, retrouver la Compétence, et la
   * ligne changeait de place au passage. La valeur se règle maintenant sur la
   * pastille elle-même ; la cible, elle, ne bouge pas.
   *
   * Zéro est refusé par le moteur — un modificateur nul n'en est pas un — donc
   * les crans l'enjambent, et c'est la croix qui retire.
   */
  import { referentiel, type ModificateurChiffre, type CibleEcrite } from '../rules'

  let {
    poses,
    ajouter,
    corriger,
    effacer,
  }: {
    poses: ModificateurChiffre[]
    ajouter: (cible: CibleEcrite, id: string, valeur: number) => void
    /** Change la valeur d'un chiffre déjà posé, sa cible inchangée. */
    corriger: (position: number, valeur: number) => void
    effacer: (position: number) => void
  } = $props()

  const r = referentiel()
  let ouvert = $state(false)
  let cible = $state<CibleEcrite>('competence')
  let id = $state('')
  let valeur = $state(-10)

  /* Ce qu'on peut viser dépend du genre de cible : une Compétence nommée, une
     Caractéristique et sa famille, ou rien du tout pour « tous les Tests ». */
  const choix = $derived(
    cible === 'competence'
      ? r.competences.map((c) => ({ id: c.id, nom: c.nom }))
      : cible === 'famille' || cible === 'caracteristique'
        ? r.caracteristiques.map((c) => ({ id: c.id, nom: c.nom }))
        : [],
  )

  const PAS = 5

  /** Un cran, qui enjambe zéro : +5 descend droit à −5, et l'inverse. */
  function cran(actuelle: number, sens: number): number {
    const suivante = actuelle + sens * PAS
    return suivante === 0 ? actuelle + sens * PAS * 2 : suivante
  }

  /* Le moteur refuse zéro. Sans ce retour en arrière, le champ garderait le
     zéro saisi à l'écran pendant que la fiche, elle, n'aurait pas bougé. */
  function saisirValeur(e: Event & { currentTarget: HTMLInputElement }, m: ModificateurChiffre) {
    const lue = Number(e.currentTarget.value)
    if (!Number.isFinite(lue) || lue === 0) {
      e.currentTarget.value = String(m.valeur)
      return
    }
    corriger(m.position, lue)
  }

  function poser() {
    if (valeur === 0) return
    if (cible !== 'tous' && id === '') return
    ajouter(cible, cible === 'tous' ? '' : id, valeur)
    ouvert = false
  }
</script>

<div class="chiffres">
  {#each poses as m (m.position)}
    <span class="pose" class:bon={m.valeur > 0}>
      <span class="reglage">
        <button
          type="button"
          class="cran"
          aria-label="Baisser le modificateur sur {m.dit}"
          onclick={() => corriger(m.position, cran(m.valeur, -1))}
        >
          −
        </button>
        <input
          class="valeur data"
          type="number"
          step={PAS}
          value={m.valeur}
          aria-label="Modificateur sur {m.dit}"
          onchange={(e) => saisirValeur(e, m)}
        />
        <button
          type="button"
          class="cran"
          aria-label="Monter le modificateur sur {m.dit}"
          onclick={() => corriger(m.position, cran(m.valeur, 1))}
        >
          +
        </button>
      </span>
      <span class="quoi">{m.dit}</span>
      <button
        type="button"
        class="cran retirer"
        aria-label="Effacer le modificateur sur {m.dit}"
        onclick={() => effacer(m.position)}
      >
        ×
      </button>
    </span>
  {/each}

  {#if ouvert}
    <div class="saisir">
      <select class="saisie" bind:value={cible} aria-label="Ce que le modificateur vise">
        <option value="competence">Une Compétence</option>
        <option value="famille">Toutes les Compétences d'une Caractéristique</option>
        <option value="caracteristique">Une Caractéristique</option>
        <option value="tous">Tous les Tests</option>
      </select>
      {#if cible !== 'tous'}
        <select class="saisie" bind:value={id} aria-label="La cible">
          <option value="">—</option>
          {#each choix as c (c.id)}
            <option value={c.id}>{c.nom}</option>
          {/each}
        </select>
      {/if}
      <input
        class="saisie court"
        type="number"
        step={PAS}
        bind:value={valeur}
        aria-label="Le modificateur"
      />
      <button type="button" class="pilule" onclick={poser}>Ajouter</button>
      <button type="button" class="lien" onclick={() => (ouvert = false)}>annuler</button>
    </div>
  {:else}
    <button type="button" class="pilule discret" onclick={() => (ouvert = true)}>
      + Chiffrer ce que ça change
    </button>
  {/if}
</div>

<style>
  .chiffres {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(var(--pas) * 0.75);
    font-size: 0.8rem;
    grid-column: 1 / -1;
  }

  /* Une pastille par chiffre : le nombre se règle à gauche, la cible se lit
     à droite, la croix retire. Tout est sur la pastille, rien n'est caché. */
  .pose {
    display: inline-flex;
    align-items: center;
    gap: calc(var(--pas) * 0.5);
    padding: calc(var(--pas) * 0.25) calc(var(--pas) * 0.5);
    border: 1px solid var(--line);
    border-left: 3px solid var(--ko);
    border-radius: var(--r);
    background: var(--surface-2);
    color: var(--ink);
  }
  /* Tout n'est pas un malus : une relique peut aider. */
  .pose.bon {
    border-left-color: var(--ok);
  }

  .reglage {
    display: inline-flex;
    align-items: center;
  }
  .cran {
    min-width: 1.6rem;
    min-height: 1.6rem;
    border: none;
    background: transparent;
    color: var(--ink-soft);
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
  }
  .cran:hover {
    color: var(--gold);
  }
  .retirer {
    color: var(--ink-soft);
  }
  .retirer:hover {
    color: var(--ko);
  }

  /* Le nombre, assez gros pour se lire à travers la table. Les flèches du
     champ sont retirées : les deux crans font le même travail, en plus large. */
  .valeur {
    width: 3.6ch;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--ko);
    font-weight: 600;
    font-size: 0.95rem;
    text-align: center;
  }
  .pose.bon .valeur {
    color: var(--ok);
  }
  .valeur::-webkit-outer-spin-button,
  .valeur::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
  .valeur {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  .quoi {
    color: var(--ink-soft);
  }

  /* Au doigt, un cran de 26 px se rate. On vise les 44 px recommandés, et
     seulement là où l'on pointe sans souris : au bureau, la pastille reste
     compacte. */
  @media (pointer: coarse) {
    .cran {
      min-width: 2.75rem;
      min-height: 2.75rem;
    }
    .valeur {
      font-size: 1.05rem;
    }
  }

  .saisir {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(var(--pas) * 0.5);
  }
  .saisir .saisie.court {
    width: 6ch;
  }
</style>
